import React, { useEffect, useMemo, useRef, useState, useCallback, memo } from 'react'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import { useThree, useLoader } from '@react-three/fiber';
import { getProjectByEkranId, getAllProjects } from './data/projects';
let cachedLampMaterial = null;
const getLampMaterial = () => {
  if (cachedLampMaterial) return cachedLampMaterial;
  cachedLampMaterial = new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: new THREE.Color("white") },
      uIntensity: { value: 30.0 }
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 uColor;
      uniform float uIntensity;
      void main() {
        gl_FragColor = vec4(uColor * uIntensity, 1.0);
      }
    `,
    transparent: false,
    toneMapped: false
  });
  return cachedLampMaterial;
};
const Screen = memo(function Screen({
  ekranId,
  geometry,
  position,
  rotation,
  scale,
  texture,
  onProjectClick
}) {
  const [hovered, setHovered] = useState(false);
  const handlePointerOver = useCallback((e) => {
    e.stopPropagation();
    setHovered(true);
    document.body.style.cursor = "pointer";
  }, []);
  const handlePointerOut = useCallback((e) => {
    e.stopPropagation();
    setHovered(false);
    document.body.style.cursor = "default";
  }, []);
  const handleClick = useCallback(() => {
    const project = getProjectByEkranId(ekranId);
    if (project && onProjectClick) {
      onProjectClick(project.id);
    }
  }, [ekranId, onProjectClick]);
  const material = useMemo(() => (
    <meshBasicMaterial
      map={texture}
      toneMapped={false}
      color={hovered ? "gray" : "white"}
    />
  ), [texture, hovered]);
  return (
    <mesh
      name={ekranId}
      geometry={geometry}
      position={position}
      rotation={rotation}
      scale={scale}
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
      onClick={handleClick}
    >
      {material}
    </mesh>
  );
});
export default function Model3({ onProjectClick, ...props }) {
  const { nodes, materials } = useGLTF('/model3.glb')
  useMemo(() => {
    materials['Material.001'].metalness = 0.8;
    materials['Material.001'].roughness = 1;
    materials['Material.001'].color.set("#222222");
    materials['Material.001'].needsUpdate = true;
  }, [materials]);
  const lampMaterial = useMemo(() => getLampMaterial(), []);
  const [video1Texture, setVideo1Texture] = useState(null);
  const videoLoadedRef = useRef(false);
  useEffect(() => {
    const loadVideo = () => {
      if (videoLoadedRef.current) return;
      videoLoadedRef.current = true;
      const video = document.createElement("video");
      video.src = process.env.PUBLIC_URL + "/video-test.mp4";
      video.loop = true;
      video.muted = true;
      video.playsInline = true;
      video.playbackRate = 1;
      video.preload = "auto";
      video.oncanplaythrough = () => {
        video.play();
        const texture = new THREE.VideoTexture(video);
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.repeat.set(1, 1);
        texture.rotation = Math.PI / -1;
        setVideo1Texture(texture);
      };
      video.load();
    };
    const timeout = setTimeout(loadVideo, 2000);
    const handleScroll = () => {
      if (window.scrollY > window.innerHeight * 1.5) {
        loadVideo();
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  const texture2Img = useLoader(
    THREE.TextureLoader,
    process.env.PUBLIC_URL + "/img/Wall.jpeg"
  );
  useMemo(() => {
    texture2Img.wrapS = THREE.RepeatWrapping;
    texture2Img.wrapT = THREE.RepeatWrapping;
    texture2Img.rotation = Math.PI / -2;
    texture2Img.repeat.set(2.4, 1);
  }, [texture2Img]);
  const renderer = useThree((state) => state.gl);
  const [loadedTextures, setLoadedTextures] = useState({});
  const textureLoadingRef = useRef({});
  const textureLoader = useMemo(() => new THREE.TextureLoader(), []);
  const placeholderTexture = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, 64, 64);
    ctx.fillStyle = '#333333';
    ctx.fillRect(20, 28, 24, 8);
    const tex = new THREE.CanvasTexture(canvas);
    tex.flipY = false;
    tex.repeat.set(-1, -1);
    return tex;
  }, []);
  // Build texture paths dynamically from projects.js data
  const texturePathsFromProjects = useMemo(() => {
    const allProjects = getAllProjects();
    return allProjects
      .sort((a, b) => a.textureIndex - b.textureIndex)
      .map(project => {
        // Extract filename from the first image path
        const imagePath = project.images[0];
        // Remove leading slash and process.env.PUBLIC_URL if present
        const cleanPath = imagePath.replace(/^\//, '');
        return cleanPath;
      });
  }, []);

  const loadTexture = useCallback((index) => {
    if (textureLoadingRef.current[index] || loadedTextures[index]) return;
    
    // Check if we have a valid texture path for this index
    if (index >= texturePathsFromProjects.length) {
      console.warn(`No texture path defined for index ${index}`);
      return;
    }
    
    textureLoadingRef.current[index] = true;
    const url = process.env.PUBLIC_URL + '/' + texturePathsFromProjects[index];
    
    textureLoader.load(
      url,
      (tex) => {
        const maxAnisotropy = renderer.capabilities.getMaxAnisotropy();
        tex.flipY = false;
        tex.rotation = 0;
        tex.center.set(0.5, 0.5);
        tex.generateMipmaps = true;
        tex.minFilter = THREE.LinearMipMapLinearFilter;
        tex.magFilter = THREE.LinearFilter;
        tex.anisotropy = maxAnisotropy;
        
        // Special handling for images that need better fitting
        // All images use contain mode to prevent cropping
        const imgAspect = tex.image.width / tex.image.height;
        const screenAspect = 1.26 / 1.2; // From Screen scale prop
        
        if (imgAspect > screenAspect) {
          // Image is wider - fit to width
          const scale = screenAspect / imgAspect;
          tex.repeat.set(-1, -scale);
          tex.offset.set(0, (1 - scale) / 2);
        } else {
          // Image is taller - fit to height
          const scale = imgAspect / screenAspect;
          tex.repeat.set(-scale, -1);
          tex.offset.set((1 - scale) / 2, 0);
        }
        
        setLoadedTextures(prev => ({ ...prev, [index]: tex }));
      },
      undefined,
      (error) => {
        console.warn(`Texture ${index} failed to load from ${url}:`, error);
        textureLoadingRef.current[index] = false;
      }
    );
  }, [renderer, loadedTextures, textureLoader, texturePathsFromProjects]);
  const getTexture = useCallback((index) => {
    return loadedTextures[index] || placeholderTexture;
  }, [loadedTextures, placeholderTexture]);
  useEffect(() => {
    const totalTextures = texturePathsFromProjects.length;
    let lastScrollY = 0;
    let scrollVelocity = 0;
    let rafId = null;
    const getTextureCountByProgress = (progress) => {
      if (progress < 0.15) return 0;        // Section 1 - no textures yet
      if (progress < 0.25) return Math.min(4, totalTextures);        // Section 2 - first 4
      if (progress < 0.35) return Math.min(8, totalTextures);        // Section 3-4 - 8 textures
      if (progress < 0.45) return Math.min(12, totalTextures);       // Section 5 - 12 textures
      if (progress < 0.55) return Math.min(16, totalTextures);       // Section 6 - 16 textures
      return totalTextures;                  // Section 7+ - all textures
    };
    const loadTexturesUpTo = (count) => {
      for (let i = 0; i < count; i++) {
        loadTexture(i);
      }
    };
    const getScrollProgress = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      return scrollHeight > 0 ? window.scrollY / scrollHeight : 0;
    };
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      scrollVelocity = Math.abs(currentScrollY - lastScrollY);
      lastScrollY = currentScrollY;
      const progress = getScrollProgress();
      let targetCount = getTextureCountByProgress(progress);
      if (scrollVelocity > 50) {
        targetCount = Math.min(targetCount + 4, totalTextures);
      }
      if (scrollVelocity > 100) {
        targetCount = Math.min(targetCount + 8, totalTextures);
      }
      loadTexturesUpTo(targetCount);
    };
    const throttledScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        handleScroll();
        rafId = null;
      });
    };
    const initialProgress = getScrollProgress();
    if (initialProgress > 0.1) {
      loadTexturesUpTo(getTextureCountByProgress(initialProgress));
    }
    window.addEventListener('scroll', throttledScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', throttledScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [loadTexture, texturePathsFromProjects.length]);
  const texture5Img = useLoader(
    THREE.TextureLoader,
    process.env.PUBLIC_URL + "/img/Wall-end.jpeg"
  );
  useMemo(() => {
    texture5Img.wrapS = THREE.RepeatWrapping;
    texture5Img.wrapT = THREE.RepeatWrapping;
    texture5Img.rotation = Math.PI / -2;
    texture5Img.repeat.set(2, 1);
  }, [texture5Img]);
  const texture6Img = useLoader(
    THREE.TextureLoader,
    process.env.PUBLIC_URL + "/img/Roof.jpeg"
  );
  useMemo(() => {
    texture6Img.wrapS = THREE.RepeatWrapping;
    texture6Img.wrapT = THREE.RepeatWrapping;
    texture6Img.rotation = Math.PI / -1;
    texture6Img.repeat.set(1, 1);
  }, [texture6Img]);
  const floorWhiteMaterial = useMemo(() => <meshBasicMaterial color={'white'} />, []);
  const floorGrayMaterial = useMemo(() => <meshBasicMaterial color={'#888888'} />, []);
  const floorDarkMaterial = useMemo(() => <meshBasicMaterial color={'#222222'} />, []);
  const urtaRekaMaterial = useMemo(() => <meshBasicMaterial color="#444444" />, []);
  const wallMaterial = useMemo(() => <meshBasicMaterial map={texture2Img} />, [texture2Img]);
  const roofMaterial = useMemo(() => <meshBasicMaterial map={texture6Img} />, [texture6Img]);
  const endWallMaterial = useMemo(() => <meshBasicMaterial map={texture5Img} side={THREE.DoubleSide} />, [texture5Img]);
  return (
    <group {...props} dispose={null}>
      <group name="Scene">
        {}
        <group name="pillar_left" position={[-8.054, 0, 2.021]} rotation={[0, 1.571, 0]} />
        {}
        <mesh
          name="screen1"
          geometry={nodes.screen1.geometry}
          material={materials['screen1.002']}
          position={[0, 0, -4.945]}
        >
          <Screen
            ekranId="ekran1"
            geometry={nodes.ekran1.geometry}
            position={[-2.994, 0.15, 5.01]}
            rotation={[Math.PI, -Math.PI / 6, Math.PI]}
            scale={[1.26, 1.2, 0.6]}
            texture={getTexture(0)}
            onProjectClick={onProjectClick}
          />
          <mesh
            name="screenLight1"
            geometry={nodes.screenLight1.geometry}
            material={materials['screen-light']}
            position={[-3.006, 0.072, 4.99]}
            rotation={[-Math.PI, Math.PI / 3, -Math.PI]}
            scale={[0.048, 0.009, 1.38]}
          />
        </mesh>
        {}
        <mesh
          name="screen2"
          geometry={nodes.screen2.geometry}
          material={materials['screen2.002']}
        >
          <Screen
            ekranId="ekran2"
            geometry={nodes.ekran2.geometry}
            position={[2.994, 0.15, -4.99]}
            rotation={[-Math.PI, Math.PI / 6, -Math.PI]}
            scale={[1.26, 1.2, 0.6]}
            texture={getTexture(1)}
            onProjectClick={onProjectClick}
          />
          <mesh
            name="screenLight2"
            geometry={nodes.screenLight2.geometry}
            material={materials['screen-light.002']}
            position={[3.009, 0.072, -5.016]}
            rotation={[0, Math.PI / 3, 0]}
            scale={[0.048, 0.009, 1.38]}
          />
        </mesh>
        {}
        <group name="pillar_left001" position={[-8.054, 0, -17.92]} rotation={[0, 1.571, 0]} />
        <group name="new-arch003" position={[0, 0, -19.941]} />
        {}
        <group name="new-arch005" position={[0, 0, -19.941]}>
          <mesh
            name="floor-reka-chap"
            geometry={nodes['floor-reka-chap'].geometry}
            position={[0.01, 0, -62.38]}
            rotation={[0, 0, -Math.PI]}
            scale={[-1, -1, -11.048]}
          >
            {floorWhiteMaterial}
          </mesh>
          <mesh
            name="floor-reka-chap"
            geometry={nodes['floor-reka-chap'].geometry}
            position={[-12.3, 0, -62.38]}
            rotation={[0, 0, -Math.PI]}
            scale={[-1, -1, -11.048]}
          >
            {floorGrayMaterial}
          </mesh>
          <mesh
            name="floor-reka-chap"
            geometry={nodes['floor-reka-chap'].geometry}
            position={[-12.43, 16.1, -62.38]}
            rotation={[0, 0, -Math.PI]}
            scale={[-1, -1, -11.048]}
          >
            {floorGrayMaterial}
          </mesh>
          <mesh
            name="floor-reka-chap"
            geometry={nodes['floor-reka-chap'].geometry}
            position={[0, -0.02, -172.67]}
            rotation={[0, 1.57, -Math.PI]}
            scale={[-1, -3, -2]}
          >
            {floorGrayMaterial}
          </mesh>
          <mesh
            name="floor-reka-chap"
            geometry={nodes['floor-reka-chap'].geometry}
            position={[0, 16.12, -172.67]}
            rotation={[0, 1.57, -Math.PI]}
            scale={[-1, -1, -2]}
          >
            {floorGrayMaterial}
          </mesh>
          <mesh
            name="floor-reka-ung"
            geometry={nodes['floor-reka-ung'].geometry}
            position={[0, 0, -62.38]}
            rotation={[0, 0, -Math.PI]}
            scale={[-1, -1, -11.048]}
          >
            {floorWhiteMaterial}
          </mesh>
          <mesh
            name="floor-reka-ung"
            geometry={nodes['floor-reka-ung'].geometry}
            position={[12.4, 0, -62.38]}
            rotation={[0, 0, -Math.PI]}
            scale={[-1, -1, -11.048]}
          >
            {floorGrayMaterial}
          </mesh>
          <mesh
            name="floor-reka-ung"
            geometry={nodes['floor-reka-ung'].geometry}
            position={[12.4, 0, -171.46]}
            rotation={[1.57, 0, -Math.PI]}
            scale={[-1, -1, -11.048]}
          >
            {floorGrayMaterial}
          </mesh>
          <mesh
            name="floor-reka-ung"
            geometry={nodes['floor-reka-ung'].geometry}
            position={[-14.65, 0, -171.48]}
            rotation={[1.57, 0.01, -Math.PI]}
            scale={[-1, -1, -11.048]}
          >
            {floorGrayMaterial}
          </mesh>
          <mesh
            name="floor-reka-ung"
            geometry={nodes['floor-reka-ung'].geometry}
            position={[12.4, 16.1, -62.38]}
            rotation={[0, 0, -Math.PI]}
            scale={[-1, -1, -11.048]}
          >
            {floorGrayMaterial}
          </mesh>
          <mesh
            name="floorr"
            geometry={nodes.floorr.geometry}
            position={[0, 0, -62.957]}
            rotation={[0, 0, -Math.PI]}
            scale={[-1, -1, -11.048]}
          >
            {floorDarkMaterial}
          </mesh>
          <mesh
            name="shifr"
            geometry={nodes.shifr.geometry}
            position={[0, 32.575, -57.139]}
            scale={[-1.466, -1, -11.661]}
            doubleSide={true}
          >
            {endWallMaterial}
          </mesh>
        </group>
        {}
        <group name="Empty003" position={[0, 0, -19.941]}>
          <mesh
            name="screen1001"
            geometry={nodes.screen1001.geometry}
            material={materials['screen1.003']}
          >
            <Screen
              ekranId="ekran3"
              geometry={nodes.ekran3.geometry}
              position={[-2.994, 0.15, 5.01]}
              rotation={[Math.PI, -Math.PI / 6, Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={getTexture(2)}
              onProjectClick={onProjectClick}
            />
            <mesh
              name="screenLight1001"
              geometry={nodes.screenLight1001.geometry}
              material={materials['screen-light.002']}
              position={[-3.006, 0.072, 4.99]}
              rotation={[-Math.PI, Math.PI / 3, -Math.PI]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>
          <mesh
            name="screen2001"
            geometry={nodes.screen2001.geometry}
            material={materials['screen2.003']}
          >
            <Screen
              ekranId="ekran4"
              geometry={nodes.ekran4.geometry}
              position={[2.994, 0.15, -4.99]}
              rotation={[-Math.PI, Math.PI / 6, -Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={getTexture(3)}
              onProjectClick={onProjectClick}
            />
            <mesh
              name="screenLight2001"
              geometry={nodes.screenLight2001.geometry}
              material={materials['screen-light.002']}
              position={[3.009, 0.072, -5.016]}
              rotation={[0, Math.PI / 3, 0]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>
        </group>
        {}
        <mesh
          name="urta-reka"
          geometry={nodes['urta-reka'].geometry}
          position={[0, 0.017, -82.92]}
          rotation={[0, 0, -Math.PI]}
          scale={[-0.082, -1, -11.048]}
        >
          {urtaRekaMaterial}
        </mesh>
        {}
        <group name="pillar_left004" position={[-8.054, 0, -57.823]} rotation={[0, 1.571, 0]} />
        <group name="pillar_left005" position={[-8.054, 0, -37.882]} rotation={[0, 1.571, 0]} />
        <group name="new-arch008" position={[0, 0, -59.844]} />
        {}
        <group name="Empty006" position={[0, 0, -59.844]}>
          <mesh
            name="screen1002"
            geometry={nodes.screen1002.geometry}
            material={materials['screen1.004']}
          >
            <Screen
              ekranId="ekran7"
              geometry={nodes.ekran7.geometry}
              position={[-2.994, 0.15, 5.01]}
              rotation={[Math.PI, -Math.PI / 6, Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={getTexture(6)}
              onProjectClick={onProjectClick}
            />
            <mesh
              name="screenLight1002"
              geometry={nodes.screenLight1002.geometry}
              material={materials['screen-light.003']}
              position={[-3.006, 0.072, 4.99]}
              rotation={[-Math.PI, Math.PI / 3, -Math.PI]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>
          <mesh
            name="screen2002"
            geometry={nodes.screen2002.geometry}
            material={materials['screen2.004']}
          >
            <Screen
              ekranId="ekran8"
              geometry={nodes.ekran8.geometry}
              position={[2.994, 0.15, -4.99]}
              rotation={[-Math.PI, Math.PI / 6, -Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={getTexture(7)}
              onProjectClick={onProjectClick}
            />
            <mesh
              name="screenLight2002"
              geometry={nodes.screenLight2002.geometry}
              material={materials['screen-light.003']}
              position={[3.009, 0.072, -5.016]}
              rotation={[0, Math.PI / 3, 0]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>
        </group>
        {}
        <group name="Empty007" position={[0, 0, -39.903]}>
          <mesh
            name="screen1003"
            geometry={nodes.screen1003.geometry}
            material={materials['screen1.005']}
          >
            <Screen
              ekranId="ekran5"
              geometry={nodes.ekran5.geometry}
              position={[-2.994, 0.15, 5.01]}
              rotation={[Math.PI, -Math.PI / 6, Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={getTexture(4)}
              onProjectClick={onProjectClick}
            />
            <mesh
              name="screenLight1003"
              geometry={nodes.screenLight1003.geometry}
              material={materials['screen-light.004']}
              position={[-3.006, 0.072, 4.99]}
              rotation={[-Math.PI, Math.PI / 3, -Math.PI]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>
          <mesh
            name="screen2003"
            geometry={nodes.screen2003.geometry}
            material={materials['screen2.005']}
          >
            <Screen
              ekranId="ekran6"
              geometry={nodes.ekran6.geometry}
              position={[2.994, 0.15, -4.99]}
              rotation={[-Math.PI, Math.PI / 6, -Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={getTexture(5)}
              onProjectClick={onProjectClick}
            />
            <mesh
              name="screenLight2003"
              geometry={nodes.screenLight2003.geometry}
              material={materials['screen-light.004']}
              position={[3.009, 0.072, -5.016]}
              rotation={[0, Math.PI / 3, 0]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>
        </group>
        {}
        <group name="pillar_left010" position={[-8.054, 0, -117.673]} rotation={[0, 1.571, 0]} />
        <group name="pillar_left011" position={[-8.054, 0, -137.614]} rotation={[0, 1.571, 0]} />
        <group name="pillar_left014" position={[-8.054, 0, -97.711]} rotation={[0, 1.571, 0]} />
        <group name="pillar_left015" position={[-8.054, 0, -77.77]} rotation={[0, 1.571, 0]} />
        <group name="new-arch020" position={[0, 0, -99.731]} />
        {}
        <group name="Empty010" position={[0, 0, -119.693]}>
          <mesh
            name="screen1004"
            geometry={nodes.screen1004.geometry}
            material={materials['screen1.006']}
          >
            <Screen
              ekranId="ekran13"
              geometry={nodes.ekran13.geometry}
              position={[-2.994, 0.15, 5.01]}
              rotation={[Math.PI, -Math.PI / 6, Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={placeholderTexture}
              onProjectClick={onProjectClick}
            />
            <mesh
              name="screenLight1004"
              geometry={nodes.screenLight1004.geometry}
              material={materials['screen-light.005']}
              position={[-3.006, 0.072, 4.99]}
              rotation={[-Math.PI, Math.PI / 3, -Math.PI]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>
          <mesh
            name="screen2004"
            geometry={nodes.screen2004.geometry}
            material={materials['screen2.006']}
          >
            <Screen
              ekranId="ekran14"
              geometry={nodes.ekran14.geometry}
              position={[2.994, 0.15, -4.99]}
              rotation={[-Math.PI, Math.PI / 6, -Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={placeholderTexture}
              onProjectClick={onProjectClick}
            />
            <mesh
              name="screenLight2004"
              geometry={nodes.screenLight2004.geometry}
              material={materials['screen-light.005']}
              position={[3.009, 0.072, -5.016]}
              rotation={[0, Math.PI / 3, 0]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>
        </group>
        {}
        <group name="Empty011" position={[0, 0, -139.634]}>
          <mesh
            name="screen1005"
            geometry={nodes.screen1005.geometry}
            material={materials['screen1.007']}
          >
            <Screen
              ekranId="ekran15"
              geometry={nodes.ekran15.geometry}
              position={[-2.994, 0.15, 5.01]}
              rotation={[Math.PI, -Math.PI / 6, Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={placeholderTexture}
              onProjectClick={onProjectClick}
            />
            <mesh
              name="screenLight1005"
              geometry={nodes.screenLight1005.geometry}
              material={materials['screen-light.006']}
              position={[-3.006, 0.072, 4.99]}
              rotation={[-Math.PI, Math.PI / 3, -Math.PI]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>
          <mesh
            name="screen2005"
            geometry={nodes.screen2005.geometry}
            material={materials['screen2.007']}
          >
            <Screen
              ekranId="ekran16"
              geometry={nodes.ekran16.geometry}
              position={[2.994, 0.15, -4.99]}
              rotation={[-Math.PI, Math.PI / 6, -Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={placeholderTexture}
              onProjectClick={onProjectClick}
            />
            <mesh
              name="screenLight2005"
              geometry={nodes.screenLight2005.geometry}
              material={materials['screen-light.006']}
              position={[3.009, 0.072, -5.016]}
              rotation={[0, Math.PI / 3, 0]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>
        </group>
        {}
        <group name="Empty014" position={[0, 0, -99.731]}>
          <mesh
            name="screen1006"
            geometry={nodes.screen1006.geometry}
            material={materials['screen1.008']}
          >
            <Screen
              ekranId="ekran11"
              geometry={nodes.ekran11.geometry}
              position={[-2.994, 0.15, 5.01]}
              rotation={[Math.PI, -Math.PI / 6, Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={placeholderTexture}
              onProjectClick={onProjectClick}
            />
            <mesh
              name="screenLight1006"
              geometry={nodes.screenLight1006.geometry}
              material={materials['screen-light.007']}
              position={[-3.006, 0.072, 4.99]}
              rotation={[-Math.PI, Math.PI / 3, -Math.PI]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>
          <mesh
            name="screen2006"
            geometry={nodes.screen2006.geometry}
            material={materials['screen2.008']}
          >
            <Screen
              ekranId="ekran12"
              geometry={nodes.ekran12.geometry}
              position={[2.994, 0.15, -4.99]}
              rotation={[-Math.PI, Math.PI / 6, -Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={placeholderTexture}
              onProjectClick={onProjectClick}
            />
            <mesh
              name="screenLight2006"
              geometry={nodes.screenLight2006.geometry}
              material={materials['screen-light.007']}
              position={[3.009, 0.072, -5.016]}
              rotation={[0, Math.PI / 3, 0]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>
        </group>
        {}
        <group name="Empty015" position={[0, 0, -79.79]}>
          <mesh
            name="screen1007"
            geometry={nodes.screen1007.geometry}
            material={materials['screen1.009']}
          >
            <Screen
              ekranId="ekran9"
              geometry={nodes.ekran9.geometry}
              position={[-2.994, 0.15, 5.01]}
              rotation={[Math.PI, -Math.PI / 6, Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={getTexture(8)}
              onProjectClick={onProjectClick}
            />
            <mesh
              name="screenLight1007"
              geometry={nodes.screenLight1007.geometry}
              material={materials['screen-light.008']}
              position={[-3.006, 0.072, 4.99]}
              rotation={[-Math.PI, Math.PI / 3, -Math.PI]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>
          <mesh
            name="screen2007"
            geometry={nodes.screen2007.geometry}
            material={materials['screen2.009']}
          >
            <Screen
              ekranId="ekran10"
              geometry={nodes.ekran10.geometry}
              position={[2.994, 0.15, -4.99]}
              rotation={[-Math.PI, Math.PI / 6, -Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={getTexture(9)}
              onProjectClick={onProjectClick}
            />
            <mesh
              name="screenLight2007"
              geometry={nodes.screenLight2007.geometry}
              material={materials['screen-light.008']}
              position={[3.009, 0.072, -5.016]}
              rotation={[0, Math.PI / 3, 0]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>
        </group>
        {}
        <group name="pillar_left017" position={[5.533, 0, 2.021]} rotation={[0, -0.017, 0]} />
        <group name="pillar_left018" position={[5.533, 0, -18.12]} rotation={[0, -1.567, 0]} />
        <group name="pillar_left019" position={[5.533, 0, -33.728]} rotation={[0, 0.021, 0]} />
        <group name="pillar_left020" position={[5.533, 0, -57.573]} rotation={[0, 1.538, 0]} />
        <group name="pillar_left021" position={[5.533, 0, -77.048]} rotation={[0, 1.538, 0]} />
        <group name="pillar_left022" position={[5.533, 0, -97.236]} rotation={[0, -Math.PI / 2, 0]} />
        <group name="pillar_left023" position={[5.533, 0, -117.643]} rotation={[-Math.PI, 1.566, -Math.PI]} />
        <group name="pillar_left024" position={[5.533, 0, -133.652]} rotation={[-Math.PI, 1.566, -Math.PI]} />
        <group name="pillar_left002" position={[5.533, 0, -173.143]} rotation={[-Math.PI, 1.566, -Math.PI]} />
        <group name="pillar_left003" position={[5.533, 0, -157.134]} rotation={[-Math.PI, 1.566, -Math.PI]} />
        {}
        <group name="pillar_left006" position={[-8.054, 0, -177.105]} rotation={[0, 1.571, 0]} />
        <group name="pillar_left007" position={[-8.054, 0, -157.164]} rotation={[0, 1.571, 0]} />
        {}
        <group name="Empty002" position={[0, 0, -159.184]}>
          <mesh
            name="screen1009"
            geometry={nodes.screen1009.geometry}
            material={materials['screen1.010']}
          >
            <Screen
              ekranId="ekran17"
              geometry={nodes.ekran17.geometry}
              position={[-2.994, 0.15, 5.01]}
              rotation={[Math.PI, -Math.PI / 6, Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={placeholderTexture}
              onProjectClick={onProjectClick}
            />
            <mesh
              name="screenLight1009"
              geometry={nodes.screenLight1009.geometry}
              material={materials['screen-light.009']}
              position={[-3.006, 0.072, 4.99]}
              rotation={[-Math.PI, Math.PI / 3, -Math.PI]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>
          <mesh
            name="screen2009"
            geometry={nodes.screen2009.geometry}
            material={materials['screen2.010']}
          >
            <Screen
              ekranId="ekran18"
              geometry={nodes.ekran18.geometry}
              position={[2.994, 0.15, -4.99]}
              rotation={[-Math.PI, Math.PI / 6, -Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={placeholderTexture}
              onProjectClick={onProjectClick}
            />
            <mesh
              name="screenLight2009"
              geometry={nodes.screenLight2009.geometry}
              material={materials['screen-light.009']}
              position={[3.009, 0.072, -5.016]}
              rotation={[0, Math.PI / 3, 0]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>
        </group>
        {}
        <mesh
          name="wall-right"
          geometry={nodes['wall-right'].geometry}
          position={[-13.6, 8.092, -84.928]}
          rotation={[0, 0, -1.561]}
          scale={[8.268, 1, 106.705]}
        >
          {wallMaterial}
        </mesh>
        <mesh
          name="devor-oxiri"
          geometry={nodes['devor-oxiri'].geometry}
          position={[-0.043, 8.158, -191.448]}
          rotation={[-Math.PI / 2, 0, 0]}
          scale={[-13.916, -1, -8.294]}
        >
          {roofMaterial}
        </mesh>
        <mesh
          name="wall-left"
          geometry={nodes['wall-left'].geometry}
          position={[13.6, 8.092, -84.928]}
          rotation={[0, 0, Math.PI / 2]}
          scale={[8.268, 1, 106.705]}
        >
          {wallMaterial}
        </mesh>
        {}
        <mesh
          name="Screen"
          geometry={nodes.Screen.geometry}
          position={[-0.009, 3.982, -179.999]}
          scale={2.571}
        >
          {video1Texture && <meshBasicMaterial map={video1Texture} />}
        </mesh>
        {}
        <group name="Empty001" position={[0, 0, -40]}>
          <mesh name="light-1-cover002" geometry={nodes['light-1-cover002'].geometry} material={materials['light2-cover.003']} position={[1.016, -14.998, 48.266]} />
          <mesh name="light-1-inner002" geometry={nodes['light-1-inner002'].geometry} material={lampMaterial} position={[1.016, -14.998, 48.266]} />
          <mesh name="light-2-cover002" geometry={nodes['light-2-cover002'].geometry} material={materials['light2-cover.002']} position={[-0.984, -14.998, 48.266]} />
          <mesh name="light-2-inner002" geometry={nodes['light-2-inner002'].geometry} material={lampMaterial} position={[-0.984, -14.998, 48.266]} />
          <mesh name="light-3-cover002" geometry={nodes['light-3-cover002'].geometry} material={materials['light2-cover.001']} position={[-2.984, -14.998, 48.266]} />
          <mesh name="light-3-inner002" geometry={nodes['light-3-inner002'].geometry} material={lampMaterial} position={[-2.984, -14.998, 48.266]} />
        </group>
        {}
        <group name="Empty008" position={[0, 0, -74.071]}>
          <mesh name="light-1-cover004" geometry={nodes['light-1-cover004'].geometry} material={materials['light2-cover.009']} position={[1.016, -14.998, 48.266]} />
          <mesh name="light-1-inner004" geometry={nodes['light-1-inner004'].geometry} material={lampMaterial} position={[1.016, -14.998, 48.266]} />
          <mesh name="light-2-cover004" geometry={nodes['light-2-cover004'].geometry} material={materials['light2-cover.008']} position={[-0.984, -14.998, 48.266]} />
          <mesh name="light-2-inner004" geometry={nodes['light-2-inner004'].geometry} material={lampMaterial} position={[-0.984, -14.998, 48.266]} />
          <mesh name="light-3-cover004" geometry={nodes['light-3-cover004'].geometry} material={materials['light2-cover.007']} position={[-2.984, -14.998, 48.266]} />
          <mesh name="light-3-inner004" geometry={nodes['light-3-inner004'].geometry} material={lampMaterial} position={[-2.984, -14.998, 48.266]} />
        </group>
        {}
        <group name="Empty012" position={[0, 0, -113.243]}>
          <mesh name="light-1-cover006" geometry={nodes['light-1-cover006'].geometry} material={materials['light2-cover.018']} position={[1.016, -14.998, 48.266]} />
          <mesh name="light-1-inner006" geometry={nodes['light-1-inner006'].geometry} material={lampMaterial} position={[1.016, -14.998, 48.266]} />
          <mesh name="light-2-cover006" geometry={nodes['light-2-cover006'].geometry} material={materials['light2-cover.017']} position={[-0.984, -14.998, 48.266]} />
          <mesh name="light-2-inner006" geometry={nodes['light-2-inner006'].geometry} material={lampMaterial} position={[-0.984, -14.998, 48.266]} />
          <mesh name="light-3-cover006" geometry={nodes['light-3-cover006'].geometry} material={materials['light2-cover.016']} position={[-2.984, -14.998, 48.266]} />
          <mesh name="light-3-inner006" geometry={nodes['light-3-inner006'].geometry} material={lampMaterial} position={[-2.984, -14.998, 48.266]} />
        </group>
        {}
        <group name="Empty016" position={[0, 0, -153.316]}>
          <mesh name="light-1-cover008" geometry={nodes['light-1-cover008'].geometry} material={materials['light2-cover.024']} position={[1.016, -14.998, 48.266]} />
          <mesh name="light-1-inner008" geometry={nodes['light-1-inner008'].geometry} material={lampMaterial} position={[1.016, -14.998, 48.266]} />
          <mesh name="light-2-cover008" geometry={nodes['light-2-cover008'].geometry} material={materials['light2-cover.023']} position={[-0.984, -14.998, 48.266]} />
          <mesh name="light-2-inner008" geometry={nodes['light-2-inner008'].geometry} material={lampMaterial} position={[-0.984, -14.998, 48.266]} />
          <mesh name="light-3-cover008" geometry={nodes['light-3-cover008'].geometry} material={materials['light2-cover.022']} position={[-2.984, -14.998, 48.266]} />
          <mesh name="light-3-inner008" geometry={nodes['light-3-inner008'].geometry} material={lampMaterial} position={[-2.984, -14.998, 48.266]} />
        </group>
        {}
        <group name="Empty018" position={[0, 0, -192.145]}>
          <mesh name="light-1-cover010" geometry={nodes['light-1-cover010'].geometry} material={materials['light2-cover.030']} position={[1.016, -14.998, 48.266]} />
          <mesh name="light-1-inner010" geometry={nodes['light-1-inner010'].geometry} material={lampMaterial} position={[1.016, -14.998, 48.266]} />
          <mesh name="light-2-cover010" geometry={nodes['light-2-cover010'].geometry} material={materials['light2-cover.029']} position={[-0.984, -14.998, 48.266]} />
          <mesh name="light-2-inner010" geometry={nodes['light-2-inner010'].geometry} material={lampMaterial} position={[-0.984, -14.998, 48.266]} />
          <mesh name="light-3-cover010" geometry={nodes['light-3-cover010'].geometry} material={materials['light2-cover.028']} position={[-2.984, -14.998, 48.266]} />
          <mesh name="light-3-inner010" geometry={nodes['light-3-inner010'].geometry} material={lampMaterial} position={[-2.984, -14.998, 48.266]} />
        </group>
        {}
        <group name="Empty019" position={[0, 0, -211.891]}>
          <mesh name="light-1-cover011" geometry={nodes['light-1-cover011'].geometry} material={materials['light2-cover.033']} position={[1.016, -14.998, 48.266]} />
          <mesh name="light-1-inner011" geometry={nodes['light-1-inner011'].geometry} material={lampMaterial} position={[1.016, -14.998, 48.266]} />
          <mesh name="light-2-cover011" geometry={nodes['light-2-cover011'].geometry} material={materials['light2-cover.032']} position={[-0.984, -16.644, 48.266]} />
          <mesh name="light-2-inner011" geometry={nodes['light-2-inner011'].geometry} material={lampMaterial} position={[-0.984, -16.644, 48.266]} />
          <mesh name="light-3-cover011" geometry={nodes['light-3-cover011'].geometry} material={materials['light2-cover.031']} position={[-2.984, -14.998, 48.266]} />
        </group>
        {}
        <group name="Empty020" position={[0, 0, -211.942]}>
          <mesh name="light-3-inner012" geometry={nodes['light-3-inner012'].geometry} material={lampMaterial} position={[-2.984, -14.998, 48.266]} />
        </group>
      </group>
    </group>
  )
}
useGLTF.preload('/model3.glb')